import React, { useCallback } from 'react';
import axiosClient from '@/utils/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateNewFolder } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as zod from 'zod';

import ProfessionalDialog from '@/components/common/professional-dialog';

interface CreateFolderDialogProps {
  onClose: () => void;
  onCreate: () => void;
  open: boolean;
  folderId?: string;
}

interface FolderApiResults {
  error?: string;
  message: string;
  folder: {
    id: string;
    folder_name: string;
  };
}

const schema = zod.object({
  folder_name: zod.string().nonempty({ message: 'Folder name is required' }),
  visibility: zod.enum(['public', 'private']).default('public'),
});

type Values = zod.infer<typeof schema>;

export function CreateFolderDialog({ open, onClose, onCreate, folderId }: CreateFolderDialogProps): React.JSX.Element {
  const defaultValues = {
    folder_name: '',
    visibility: 'public' as const,
  } satisfies Values;

  const createFolderRequest = useMutation({
    mutationFn: async (values: Values): Promise<FolderApiResults> => {
      try {
        const url = folderId ? `/api/v1/folders/${folderId}/` : '/api/v1/folders';
        const response = await axiosClient.post(url, values);

        return response.data;
      } catch (error) {
        if ((error as any).status === 403) {
          throw new Error('You are not authorized to create a folder');
        }

        throw new Error('An error occurred while creating a folder');
      }
    },
  });

  const {
    handleSubmit,
    reset,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Values>({ defaultValues, resolver: zodResolver(schema) });

  const handleCreate = useCallback(
    async (values: Values) => {
      console.log('✅ Form submitted successfully!');
      console.log('📝 Payload:', values);
      console.log('❌ Form errors:', errors);

      await axiosClient.get('sanctum/csrf-cookie');

      try {
        const response = await createFolderRequest.mutateAsync(values);

        if (response.error) {
          return;
        }
        toast.success(response.message);
        reset();
        onCreate();
      } catch (error) {
        console.error('🚨 Create folder error:', error);
        toast.error((error as any).message);
      }
    },
    [errors]
  );

  return (
    <ProfessionalDialog
      autoHeight
      fullWidth
      icon={<CreateNewFolder />}
      maxWidth="sm"
      onClose={onClose}
      open={open}
      subtitle="Enter a name for your new folder"
      title="Create Folder"
      variant="elevated"
    >
      <form
        onSubmit={(e) => {
          console.log('🔄 Form submission triggered');
          console.log('📋 Form element:', e.currentTarget);
          console.log('🎯 Form action:', e.currentTarget.action);
          handleSubmit(handleCreate)(e);
        }}
      >
        <Box sx={{ mt: 2 }}>
          <Stack spacing={3}>
            <FormControl error={Boolean(errors.folder_name)} fullWidth>
              <InputLabel htmlFor="folder_name">Folder Name</InputLabel>
              <OutlinedInput
                {...register('folder_name')}
                fullWidth
                id="folder_name"
                placeholder="Enter a folder name"
                type="text"
              />
              <FormHelperText error>{errors.folder_name?.message}</FormHelperText>
            </FormControl>

            <FormControl fullWidth>
              <FormControlLabel
                control={
                  <Switch
                    {...register('visibility')}
                    checked={watch('visibility') === 'private'}
                    onChange={(e) => { setValue('visibility', e.target.checked ? 'private' : 'public'); }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography color="text.secondary" variant="body2">
                      {watch('visibility') === 'private' ? 'Private' : 'Public'}
                    </Typography>
                    <Typography color="text.secondary" variant="caption">
                      {watch('visibility') === 'private'
                        ? '(Only community managers and account admins can see this folder)'
                        : '(All community members can see this folder)'}
                    </Typography>
                  </Box>
                }
              />
            </FormControl>

            {/* Form Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 2 }}>
              <Button onClick={onClose} variant="outlined">
                Cancel
              </Button>
              <LoadingButton color="primary" loading={createFolderRequest.isPending} type="submit" variant="contained">
                Create Folder
              </LoadingButton>
            </Box>
          </Stack>
        </Box>
      </form>
    </ProfessionalDialog>
  );
}
